import { PERMISSIONS, type Permission } from '@zeroth/shared';
import { STORE_SETTINGS_PERMISSIONS } from '../../features/settings/settingsNavigation';

export interface NavItem {
  sidebarLabel: string;
  bookmarkLabel: string;
  href: string;
  permissions?: Permission[];
}

export interface NavSection {
  label: string;
  href?: string;
  bookmarkLabel?: string;
  permissions?: Permission[];
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    bookmarkLabel: 'Dashboard',
    permissions: [PERMISSIONS.DASHBOARD_VIEW],
    items: [],
  },
  {
    label: 'Zeno',
    href: '/agent',
    bookmarkLabel: 'Zeno',
    permissions: [PERMISSIONS.AI_USE],
    items: [],
  },
  {
    label: 'Repricer',
    items: [
      {
        sidebarLabel: 'Listings',
        bookmarkLabel: 'Repricer Listings',
        href: '/repricer/listings',
        permissions: [PERMISSIONS.REPRICER_MANAGE],
      },
    ],
  },
  {
    label: 'Restock',
    items: [
      {
        sidebarLabel: 'Suggestions',
        bookmarkLabel: 'Restock Suggestions',
        href: '/restock/suggestions',
        permissions: [PERMISSIONS.RESTOCK_VIEW],
      },
      {
        sidebarLabel: 'Purchase Orders',
        bookmarkLabel: 'Purchase Orders',
        href: '/restock/pos',
        permissions: [PERMISSIONS.PURCHASE_ORDERS_VIEW],
      },
      {
        sidebarLabel: 'Suppliers',
        bookmarkLabel: 'Suppliers',
        href: '/restock/suppliers',
        permissions: [PERMISSIONS.SUPPLIERS_VIEW],
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        sidebarLabel: 'Products',
        bookmarkLabel: 'Products',
        href: '/products',
        permissions: [PERMISSIONS.PRODUCTS_VIEW],
      },
      {
        sidebarLabel: 'Expiration Lots',
        bookmarkLabel: 'Expiration Lots',
        href: '/inventory/expiration-lots',
        permissions: [PERMISSIONS.INVENTORY_VIEW],
      },
      {
        sidebarLabel: 'Audit',
        bookmarkLabel: 'Inventory Audit',
        href: '/inventory/audit',
        permissions: [PERMISSIONS.INVENTORY_VIEW],
      },
    ],
  },
  {
    label: 'Shipping',
    items: [
      {
        sidebarLabel: 'Draft',
        bookmarkLabel: 'Draft FBA Shipments',
        href: '/workflows/draft-shipments',
        permissions: [
          PERMISSIONS.SHIPMENTS_VIEW,
          PERMISSIONS.SHIPMENTS_CREATE,
          PERMISSIONS.SHIPMENTS_MANAGE,
        ],
      },
      {
        sidebarLabel: 'Working',
        bookmarkLabel: 'Working FBA Shipments',
        href: '/workflows/working-shipments',
        permissions: [PERMISSIONS.SHIPMENTS_VIEW],
      },
      {
        sidebarLabel: 'Shipped',
        bookmarkLabel: 'Shipped to Amazon',
        href: '/workflows/shipped',
        permissions: [PERMISSIONS.SHIPMENTS_VIEW, PERMISSIONS.SHIPPING_MANAGE],
      },
    ],
  },
  {
    label: 'Reviews',
    items: [
      {
        sidebarLabel: 'Solicitations',
        bookmarkLabel: 'Solicitations',
        href: '/solicitations',
        permissions: [PERMISSIONS.SOLICITATIONS_VIEW],
      },
      {
        sidebarLabel: 'Alert Center',
        bookmarkLabel: 'Alert Center',
        href: '/alert-center',
        permissions: [PERMISSIONS.ALERTS_VIEW],
      },
    ],
  },
  {
    label: 'Research',
    items: [
      {
        sidebarLabel: 'Product Research',
        bookmarkLabel: 'Research Scans',
        href: '/research',
        permissions: [PERMISSIONS.RESEARCH_VIEW],
      },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      {
        sidebarLabel: 'Analytics',
        bookmarkLabel: 'Analytics',
        href: '/analytics',
        permissions: [PERMISSIONS.ANALYTICS_VIEW],
      },
      {
        sidebarLabel: 'Reports',
        bookmarkLabel: 'Reports',
        href: '/reports',
        permissions: [PERMISSIONS.REPORTS_VIEW],
      },
    ],
  },
  {
    label: 'Operational Readiness',
    href: '/readiness',
    bookmarkLabel: 'Operational Readiness',
    permissions: [PERMISSIONS.READINESS_VIEW],
    items: [],
  },
  {
    label: 'Config',
    items: [
      {
        sidebarLabel: 'Account',
        bookmarkLabel: 'Account Settings',
        href: '/settings/account',
      },
      {
        sidebarLabel: 'Operations',
        bookmarkLabel: 'Operations Settings',
        href: '/settings/operations',
        permissions: [...STORE_SETTINGS_PERMISSIONS, PERMISSIONS.CARRIER_CONFIG_MANAGE],
      },
      {
        sidebarLabel: 'Store',
        bookmarkLabel: 'Store Settings',
        href: '/settings/store',
        permissions: STORE_SETTINGS_PERMISSIONS,
      },
    ],
  },
];

const bookmarkableItems: Array<Pick<NavItem, 'href' | 'bookmarkLabel' | 'permissions'>> = navSections.flatMap((section) => [
  ...(section.href && section.bookmarkLabel
    ? [{ href: section.href, bookmarkLabel: section.bookmarkLabel, permissions: section.permissions }]
    : []),
  ...section.items.map((item) => ({
    href: item.href,
    bookmarkLabel: item.bookmarkLabel,
    permissions: item.permissions,
  })),
]);

const bookmarkLabelsByHref = new Map(
  bookmarkableItems.map((item) => [item.href, item.bookmarkLabel])
);

const bookmarkableItemsByLongestHref = [...bookmarkableItems].sort(
  (a, b) => b.href.length - a.href.length
);

export const allNavHrefs: string[] = bookmarkableItems.map((item) => item.href);

export function getBookmarkMatchPath(href: string): string {
  const queryIndex = href.indexOf('?');
  const hashIndex = href.indexOf('#');
  const endIndexes = [queryIndex, hashIndex].filter((index) => index >= 0);

  if (endIndexes.length === 0) return href;

  return href.slice(0, Math.min(...endIndexes));
}

export function getBookmarkLabel(href: string): string {
  const matchPath = getBookmarkMatchPath(href);
  const exactLabel = bookmarkLabelsByHref.get(matchPath);
  if (exactLabel) return exactLabel;

  const parentMatch = bookmarkableItemsByLongestHref.find((item) =>
    matchPath.startsWith(`${item.href}/`)
  );

  return parentMatch?.bookmarkLabel ?? href;
}

export function canAccessNavItem(required: Permission[] | undefined, permissions: readonly Permission[]): boolean {
  return !required?.length || required.some((permission) => permissions.includes(permission));
}

/** Only actual sidebar destinations may surface as pins, never inferred parent routes. */
export function getVisibleBookmarks(hrefs: string[], permissions: readonly Permission[]): string[] {
  return hrefs.filter((href) => {
    const item = bookmarkableItems.find((candidate) => candidate.href === getBookmarkMatchPath(href));
    return item !== undefined && canAccessNavItem(item.permissions, permissions);
  });
}
